import { create } from 'zustand';
import type { RoomNotice } from '../api/chat-notice';

/**
 * 방 공지 상태 (room-notice — PC 화면).
 *
 * 왜 store 인가: 공지를 **두 곳이 함께** 본다 — 대화 위 고정 띠(ChatNoticeBanner)와
 * 정보 패널의 '공지 등록'(ChatDetailPanel). 둘은 형제 컴포넌트라 한쪽에서 고친 공지가
 * 다른 쪽에 바로 보이려면 상태가 밖에 있어야 한다. prop 으로 내리면 ChatArea 의 기존
 * 시그니처를 두 번 건드리게 되므로 store 를 새로 둔다(기존 store 무수정).
 *
 * 값의 뜻:
 *   undefined = 아직 안 불러왔다 (띠를 그리지 않는다 — 깜빡임 방지)
 *   null      = 불러왔는데 공지가 없다
 *   객체      = 공지가 있다
 *
 * 접기(collapsed)는 **공지 본문을 키로** 저장한다. 시각이나 방 id 로 잡으면 공지가 바뀌어도
 * 접힌 채로 남아 새 공지를 놓친다 — 본문이 바뀌면 다시 뜨는 것이 맞다.
 */

const LS_COLLAPSED = 'chat.notice.collapsed';

type CollapsedMap = Record<string, string>;

function readCollapsed(): CollapsedMap {
  try {
    const raw = localStorage.getItem(LS_COLLAPSED);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    // 값이 문자열인 칸만 취한다 — 옛 형식/손상된 값이 섞여도 나머지는 산다.
    const out: CollapsedMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function persistCollapsed(map: CollapsedMap): void {
  try {
    localStorage.setItem(LS_COLLAPSED, JSON.stringify(map));
  } catch {
    /* localStorage 비활성 — 세션 안에서는 그대로 동작한다 */
  }
}

interface ChatNoticeState {
  /** roomId → 공지. undefined 는 미조회. */
  noticeByRoom: Record<string, RoomNotice | null>;
  /** roomId → 접어 둔 공지 본문. */
  collapsedByRoom: CollapsedMap;
  setNotice: (roomId: string, notice: RoomNotice | null) => void;
  /** 이 본문의 공지를 접는다. 다른 본문으로 바뀌면 다시 보인다. */
  collapse: (roomId: string, text: string) => void;
  /** 접기를 푼다(다시 보이게). */
  expand: (roomId: string) => void;
}

export const useChatNoticeStore = create<ChatNoticeState>((set) => ({
  noticeByRoom: {},
  collapsedByRoom: readCollapsed(),

  setNotice: (roomId, notice) =>
    set((s) => ({ noticeByRoom: { ...s.noticeByRoom, [roomId]: notice } })),

  collapse: (roomId, text) =>
    set((s) => {
      const next = { ...s.collapsedByRoom, [roomId]: text };
      persistCollapsed(next);
      return { collapsedByRoom: next };
    }),

  expand: (roomId) =>
    set((s) => {
      if (!(roomId in s.collapsedByRoom)) return s;
      const next = { ...s.collapsedByRoom };
      delete next[roomId];
      persistCollapsed(next);
      return { collapsedByRoom: next };
    }),
}));

/** 이 방의 공지를 지금 띠로 보여야 하는가 — 공지가 있고, 그 본문을 접어 두지 않았을 때. */
export function shouldShowNotice(
  notice: RoomNotice | null | undefined,
  collapsedText: string | undefined,
): notice is RoomNotice {
  if (!notice || !notice.text) return false;
  return collapsedText !== notice.text;
}
